import { useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";

const usePrecoRegra = ({ filters, setLoading, produto, filial, microrregiao, cluster, cidade, skip, take }) => {
  const [total, setTotal] = useState(0);
  const [totalRenderizado, setTotalRenderizado] = useState(0);
  const [precosRegraDexie, setPrecosRegraDexie] = useState([]);
  const [precoRegraFilters, setPrecoRegraFilters] = useState([]);

  const precoRegraQuery = (order = true) => {
    if (typeof setLoading === 'function') setLoading(true);

    let precos = db.tabelaPreco;

    // Filtros aplicados
    if (filters?.grupoProduto || filters?.produto || filters?.filial || filters?.cidade || filters?.microrregiao || filters?.grupoSegmento || filters?.segmento || filters?.cluster) {
      if (order) precos = precos.orderBy('nomeFilial');

      // Aplica os filtros
      precos = precos.filter((precoRegra) => {
        return (
          (filters?.grupoProduto ? (precoRegra.grupoProdutoId ?? precoRegra.grupoMaterialId) === filters?.grupoProduto : true) &&
          (filters?.produto ? precoRegra.produtoId === filters?.produto : true) &&
          (filters?.filial ? precoRegra.filialId === filters?.filial : true) &&
          (filters?.cidade ? precoRegra.cidadeId === filters?.cidade : true) &&
          (filters?.microrregiao ? precoRegra.microrregiaoId === filters?.microrregiao : true) &&
          (filters?.grupoSegmento ? precoRegra.grupoSegmentoId === filters?.grupoSegmento : true) &&
          (filters?.segmento ? precoRegra.segmentoId === filters?.segmento : true) &&
          (filters?.cluster ? precoRegra.clusterId === filters?.cluster : true)
        );
      });
    } else {
      if (order) precos = precos.orderBy('nomeFilial');
    }

    return precos;
  };


  useEffect(() => {
    const fetchPrecos = async () => {
      if (typeof setLoading === 'function') setLoading(true);

      const precosQuery = precoRegraQuery(filters);
      let filteredPrecos = precosQuery.filter((preco) => preco.ativo);

      setTotal(await filteredPrecos.count());

      const precos = [];
      await filteredPrecos
        .offset(0)
        .limit(skip + take)
        .each((preco) => {
          precos.push(preco);
        });

      setTotalRenderizado(precos.length);

      setPrecosRegraDexie(precos);
      if (typeof setLoading === 'function') setLoading(false);
    };

    fetchPrecos();
  }, [filters, skip]);

  const precosRegra = useMemo(() => {
    const retorno = {};
    // Iterando uma vez sobre os precosRegraDexie
    for (const obj of (precosRegraDexie || [])) {
      if (!retorno[obj.nomeFilial]) {
        retorno[obj.nomeFilial] = {
          header: {
            icon: '',
            title: obj.nomeFilial
          },
          accordion: {
            enabled: false,
            label: obj.nomeFilial,
          },
          hideNegociacao: true,
          hidePrazo: true,
          showMicrorregiao: true,
          produtos: []
        };
      }

      // Adicionando produtos diretamente sem a necessidade de filtro e map
      retorno[obj.nomeFilial].produtos.push({
        ...obj,
        color: {
          fob: '#FFF',
          cif: '#FFF',
        }
      });
    }

    // Finaliza o carregamento
    if (typeof setLoading == "function") setLoading(false);

    // Retorna resultado dependendo dos filtros
    if (filters?.grupoProduto || filters?.produto || filters?.filial || filters?.cidade || filters?.microrregiao || filters?.grupoSegmento || filters?.segmento || filters?.cluster) {
      return Object.values(retorno);
    }

    return [];
  }, [precosRegraDexie, filters]);

  useEffect(() => {
    const fetchFiltersPrecoRegra = async () => {
      let precosQuery = db.tabelaPreco;
      if (microrregiao && cluster) precosQuery = precosQuery.filter((a) => a.microrregiaoId === microrregiao && (a.clusterId === cluster || !a.clusterId));
      else if (microrregiao) precosQuery = precosQuery.filter((a) => a.microrregiaoId === microrregiao);
      else precosQuery = precosQuery.filter((a) => a.ativo === true);
      const precos = [];
      await precosQuery.each((precoRegra) => {
        precos.push(precoRegra);
      })

      setPrecoRegraFilters(precos);
    }
    fetchFiltersPrecoRegra();
  }, [microrregiao, cluster]);

  const filtrosDisponiveisPrecoRegra = useMemo(() => {
    if (precoRegraFilters) {
      const microrregiaoIds = [...new Set(precoRegraFilters.map(row => row.microrregiaoId).filter(id => id !== undefined))];
      const opcoesFiltradas = precoRegraFilters.filter(precoRegra => {
        return (filters?.grupoProduto ? precoRegra.grupoMaterialId === filters?.grupoProduto : true) &&
          (filters?.produto ? precoRegra.produtoId === filters?.produto : true) &&
          (filters?.filial ? precoRegra.filialId === filters?.filial : true) &&
          (filters?.microrregiao ? precoRegra.microrregiaoId === filters?.microrregiao : true) &&
          (filters?.cidade ? precoRegra.cidadeId === filters?.cidade : true) &&
          (filters?.grupoSegmento ? precoRegra.grupoSegmentoId === filters?.grupoSegmento : true) &&
          (filters?.segmento ? precoRegra.segmentoId === filters?.segmento : true) &&
          (filters?.cluster ? precoRegra.clusterId === filters?.cluster : true)
      });

      const produtoIds = [...new Set(opcoesFiltradas.map(row => row.produtoId).filter(id => id !== undefined))];
      const grupoMaterialIds = [...new Set(opcoesFiltradas.map(row => row.grupoMaterialId).filter(id => id !== undefined))]
      const filiaisIds = [...new Set(opcoesFiltradas.map(row => row.filialId).filter(id => id !== undefined))]
      const cidadeIds = [...new Set(opcoesFiltradas.map(row => row.cidadeId).filter(id => id !== undefined))];
      const grupoSegmentoIds = [...new Set(opcoesFiltradas.map(row => row.grupoSegmentoId).filter(id => id !== undefined))];
      const segmentoIds = [...new Set(opcoesFiltradas.map(row => row.segmentoId).filter(id => id !== undefined))];
      const clusterIds = [...new Set(opcoesFiltradas.map(row => row.clusterId).filter(id => id !== undefined))];
      const familiaIds = [...new Set(opcoesFiltradas.map(row => row.familiaId).filter(id => id !== undefined))];
      const unidadesMedidaIds = [...new Set(opcoesFiltradas.map(row => row.unidadeMedidaId).filter(id => id !== undefined))];

      return {
        grupoMaterialDisponiveis: grupoMaterialIds,
        cidadesDisponiveis: cidadeIds,
        clustersDisponiveis: clusterIds,
        produtosDisponiveis: produtoIds,
        filiaisDisponiveis: filiaisIds,
        microrregioesDisponiveis: microrregiaoIds,
        grupoSegmentoDisponiveis: grupoSegmentoIds,
        segmentosDisponiveis: segmentoIds,
        familiasDisponiveis: familiaIds,
        unidadesMedidaDisponiveis: unidadesMedidaIds
      };
    }
    return {};
  }, [precoRegraFilters, filters])


  const [precoRegra, setPrecoRegra] = useState(null);

  useEffect(() => {
    const fetchPrecoRegra = async () => {
      if (produto && filial && microrregiao) {
        const result = [];

        await db.tabelaPreco.where({ produtoId: produto, filialId: filial, microrregiaoId: microrregiao }).each((item) => result.push(item));

        if (result.length > 0) {
          let preco = result.find((a) => a.clusterId === cluster) || result.find((a) => a.cidadeId === cidade) || result[0];
          setPrecoRegra(preco);
        }
      }
    };

    fetchPrecoRegra();
  }, [produto, filial, microrregiao, cluster, cidade]);

  return { precosRegra, precosRegraDexie, total, precoRegra, filtrosDisponiveisPrecoRegra, totalRenderizado }
}

export default usePrecoRegra;